import { action, autorun, computed, IReactionDisposer, makeObservable, observable } from 'mobx';
import type { AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import {
  CandidateEditProfileEmptyResponse,
  NewCVCandidateResponseDTO,
} from 'candidate/types';
import { ApiKey, HttpClient } from 'shared/libs/http';
import { AsyncState } from 'shared/libs/state';
import { ApiResponseDto } from 'shared/types';
import {
  CountryCityCity,
  formatCountryCityLabel,
} from 'shared/components/country-city-selector';

import { DesiredPositionStore } from './desired-position/desired-position.store';
import { WorkExperienceStore } from './work-experience/work-experience.store';
import { EducationInfoStore } from './education-info/education-info.store';
import { AdditionalInfoStore } from './additonal-info/additonal-info.store';

export const CreateResumeStep = {
  DESIRED_POSITION: 'desired_position',
  WORK_EXPERIENCE: 'work_experience',
  EDUCATION_INFO: 'education_info',
  ADDITIONAL_INFO: 'additional_info',
} as const;

export type CreateResumeStep = (typeof CreateResumeStep)[keyof typeof CreateResumeStep];

export const isCreateResumeStep = (value: string | null): value is CreateResumeStep =>
  Object.values(CreateResumeStep).includes(value as CreateResumeStep);

export const CreateResumeMode = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
  COPY: 'COPY',
  ADMIN_EDIT: 'ADMIN_EDIT',
} as const;

export type CreateResumeMode = (typeof CreateResumeMode)[keyof typeof CreateResumeMode];

type SubmitResumeResponse = ApiResponseDto<unknown>;

type ApiOption = {
  value: number | string | null;
  label: string;
};

type SubmitResumePayload = {
  id?: number | string;
  city: ApiOption | null;
  position: ApiOption;
  desired_category: Array<{
    category: number | null;
    sub_category: ApiOption[];
  }>;
  work_type: ApiOption[];
  salary_min: number | null;
  salary_max: number | null;
  desired_country: number | null;
  relocations: ApiOption[];
  salary_currency: number | null;
  experiences: Array<{
    position: ApiOption;
    organization: ApiOption;
    industry: ApiOption[];
    exp_country: number | null;
    exp_city: ApiOption | '';
    from_month_exp: number | null;
    from_year_exp: number | null;
    to_month_exp: number | null;
    to_year_exp: number | null;
    still_working: 0 | 1;
    achievements: string;
  }>;
  educations: Array<{
    edu_type: string;
    institution: ApiOption;
    faculty: ApiOption;
    specialty: ApiOption;
    from_month_edu: number | null;
    from_year_edu: number | null;
    to_month_edu: number | null;
    to_year_edu: number | null;
    still_learning: boolean;
  }>;
  additional_educations: Array<{
    course_name: string | null;
    institution_add: string | null;
    diploma_type: string | null;
    course_link: string | null;
    certificate: string;
    from_month_edu_add: number | null;
    from_year_edu_add: number | null;
    to_month_edu_add: number | null;
    to_year_edu_add: number | null;
    still_learning_add: boolean;
  }>;
  video_cv_link: string;
  about_yourself: string;
  skills: ApiOption[];
  languages: Array<{
    lng: number | null;
    lng_skill: string;
  }>;
  driver_license: number;
  driver_category: ApiOption[];
  visibility: ApiOption | null;
  company_whitelist: ApiOption[];
  company_blacklist: ApiOption[];
};

export class CreateResumeStore {
  activeStep: CreateResumeStep = CreateResumeStep.DESIRED_POSITION;
  readonly attemptedSteps = observable.set<CreateResumeStep>();
  readonly loadInitialValuesState = new AsyncState<
    CandidateEditProfileEmptyResponse | NewCVCandidateResponseDTO
  >();
  readonly submitResumeState = new AsyncState<SubmitResumeResponse>();
  private submitResumeId?: number | string;
  private profileCity: ApiOption | null = null;
  private readonly valuesLoggerDisposer: IReactionDisposer;

  readonly stepOrder: CreateResumeStep[] = [
    CreateResumeStep.DESIRED_POSITION,
    CreateResumeStep.WORK_EXPERIENCE,
    CreateResumeStep.EDUCATION_INFO,
    CreateResumeStep.ADDITIONAL_INFO,
  ];

  constructor(
    public readonly desiredPosition: DesiredPositionStore,
    public readonly workExperience: WorkExperienceStore,
    public readonly educationInfo: EducationInfoStore,
    public readonly additionalInfo: AdditionalInfoStore,
    initialStep: CreateResumeStep = CreateResumeStep.DESIRED_POSITION,
    public readonly mode: CreateResumeMode = CreateResumeMode.CREATE,
    public readonly resumeId?: string
  ) {
    this.activeStep = initialStep;
    this.submitResumeId = this.normalizeResumeId(resumeId);

    makeObservable(this, {
      activeStep: observable,
      isBootstrapping: computed,
      currentStepIndex: computed,
      canGoBack: computed,
      canGoNext: computed,
      valid: computed,
      isCurrentStepValid: computed,
      isSubmitting: computed,
      valuesSnapshot: computed,
      loadInitialValues: action.bound,
      submitResume: action.bound,
      goBack: action.bound,
      goNext: action.bound,
      goToStep: action.bound,
      setActiveStep: action.bound,
    });

    this.valuesLoggerDisposer = autorun(() => {
      console.log('CreateResumeStore values changed', this.valuesSnapshot);
    });
  }

  get currentStepIndex(): number {
    return this.stepOrder.indexOf(this.activeStep);
  }

  get isBootstrapping(): boolean {
    return this.loadInitialValuesState.status === 'pending';
  }

  get isSubmitting(): boolean {
    return this.submitResumeState.status === 'pending';
  }

  get canGoBack(): boolean {
    return this.currentStepIndex > 0;
  }

  get canGoNext(): boolean {
    return this.currentStepIndex < this.stepOrder.length - 1;
  }

  get valid(): boolean {
    return (
      this.desiredPosition.valid &&
      this.workExperience.valid &&
      this.educationInfo.valid &&
      this.additionalInfo.valid
    );
  }

  get isCurrentStepValid(): boolean {
    switch (this.activeStep) {
      case CreateResumeStep.DESIRED_POSITION:
        return this.desiredPosition.valid;
      case CreateResumeStep.WORK_EXPERIENCE:
        return this.workExperience.valid;
      case CreateResumeStep.EDUCATION_INFO:
        return this.educationInfo.valid;
      case CreateResumeStep.ADDITIONAL_INFO:
        return this.additionalInfo.valid;
      default:
        return false;
    }
  }

  get valuesSnapshot() {
    return {
      mode: this.mode,
      resumeId: this.resumeId,
      activeStep: this.activeStep,
      isBootstrapping: this.isBootstrapping,
      desiredPosition: {
        position: this.desiredPosition.position,
        desiredCategories: this.desiredPosition.desiredCategories.map((group) => ({
          categoryId: group.categoryId,
          specializations: group.specializations.values.map((item) => ({
            id: item.id,
            name: item.name,
          })),
        })),
        workTypes: this.desiredPosition.workTypes.values.map((item) => ({
          value: item.value,
          label: item.label,
        })),
        salaryMin: this.desiredPosition.salaryMin,
        salaryMax: this.desiredPosition.salaryMax,
        desiredCountry: this.desiredPosition.desiredCountry,
        desiredCity: this.desiredPosition.desiredCity,
        salaryCurrency: this.desiredPosition.salaryCurrency,
      },
      workExperience: this.workExperience.experiences.map((experience) => ({
        position: experience.position,
        organization: experience.organization,
        industries: experience.industries.values.map((industry) => ({
          id: industry.id,
          name: industry.name,
        })),
        expCountry: experience.expCountry,
        expCity: experience.expCity,
        fromMonth: experience.fromMonth,
        fromYear: experience.fromYear,
        toMonth: experience.toMonth,
        toYear: experience.toYear,
        stillWorking: experience.stillWorking,
        achievements: experience.achievements,
      })),
      educationInfo: this.educationInfo.educations.map((education) => ({
        eduType: education.eduType,
        institution: education.institution,
        faculty: education.faculty,
        specialty: education.specialty,
        fromMonth: education.fromMonth,
        fromYear: education.fromYear,
        toMonth: education.toMonth,
        toYear: education.toYear,
        stillLearning: education.stillLearning,
      })),
      additionalEducationInfo: this.educationInfo.additionalEducations.map((education) => ({
        courseName: education.courseName,
        institution: education.institution,
        documentName: education.documentName,
        courseLink: education.courseLink,
        certificate: education.certificate,
        fromMonth: education.fromMonth,
        fromYear: education.fromYear,
        toMonth: education.toMonth,
        toYear: education.toYear,
        stillLearning: education.stillLearning,
      })),
      additionalInfo: {
        aboutYourself: this.additionalInfo.aboutYourself,
        videoCvLink: this.additionalInfo.videoCvLink,
        skills: this.additionalInfo.skills.values.map((skill) => ({
          id: skill.id,
          name: skill.name,
        })),
        languages: this.additionalInfo.languages.map((language) => ({
          languageId: language.languageId,
          skillId: language.skillId,
        })),
        driverLicense: this.additionalInfo.driverLicense,
        driverCategories: this.additionalInfo.driverCategories.values.map((category) => ({
          id: category.id,
          name: category.name,
        })),
        visibility: this.additionalInfo.visibility,
        companyWhitelist: this.additionalInfo.companyWhitelist.selected.map((company) => ({
          id: company.id,
          name: company.name,
        })),
        companyBlacklist: this.additionalInfo.companyBlacklist.selected.map((company) => ({
          id: company.id,
          name: company.name,
        })),
      },
    };
  }

  async loadInitialValues(isMounted: () => boolean = () => true): Promise<void> {
    await this.loadInitialValuesState.run(
      () => this.loadInitialValuesRequest(),
      {
        onSuccess: (response) => {
          if (!isMounted() || response.status !== 200) {
            return;
          }

          this.submitResumeId =
            this.mode === CreateResumeMode.ADMIN_EDIT
              ? this.normalizeResumeId(this.resumeId) ?? this.submitResumeId
              : this.resolveSubmitResumeId(response.response) ?? this.submitResumeId;
          this.profileCity =
            this.toApiOption(response.response.userdetail?.city) ?? this.profileCity;

          const hydrateProfile = this.mode !== CreateResumeMode.CREATE;

          this.desiredPosition.update(response.response, {
            hydrateProfile,
            appendCopySuffix: this.mode === CreateResumeMode.COPY,
          });
          this.workExperience.update(response.response, { hydrateProfile });
          this.educationInfo.update(response.response, { hydrateProfile });
          this.additionalInfo.update(response.response, { hydrateProfile });
        },
        onError: (error) => {
          console.error(error);
        },
      }
    );
  }

  private loadInitialValuesRequest(): Promise<
    CandidateEditProfileEmptyResponse | NewCVCandidateResponseDTO
  > {
    if (this.mode === CreateResumeMode.CREATE) {
      return HttpClient.post<NewCVCandidateResponseDTO>(
        '/users/newCVCandidate',
        null
      );
    }

    if (this.mode === CreateResumeMode.ADMIN_EDIT && this.resumeId) {
      return HttpClient.post<CandidateEditProfileEmptyResponse>(
        `/admin/resume/edit/${this.resumeId}`,
        null,
        this.adminRequestConfig
      );
    }

    if (this.resumeId) {
      return HttpClient.post<CandidateEditProfileEmptyResponse>(
        `/candidates/editCv/${this.resumeId}`,
        null
      );
    }

    return HttpClient.get<CandidateEditProfileEmptyResponse>(
      '/candidates/editProfile'
    );
  }

  async submitResume({
    onSuccess,
    onError,
  }: {
    onSuccess?: (response: SubmitResumeResponse) => void | Promise<void>;
    onError?: (message: string, status?: number) => void | Promise<void>;
  } = {}): Promise<SubmitResumeResponse | null> {
    if (!this.valid) {
      this.markAllStepsAttempted();
      this.focusFirstInvalidStep();
      return null;
    }

    return this.submitResumeState.run(
      () => HttpClient.post<SubmitResumeResponse>(
        this.submitEndpoint,
        this.buildSubmitPayload(),
        this.adminRequestConfig
      ),
      {
        onSuccess: async (response) => {
          if (response.status === 200) {
            await onSuccess?.(response);
            return;
          }

          await onError?.(response.message, response.status);
        },
        onError: async (error) => {
          await onError?.(error.message, error.status);
        },
      }
    );
  }

  private get submitEndpoint(): string {
    if (this.mode === CreateResumeMode.ADMIN_EDIT) {
      const resumeId = this.submitResumeId ?? this.normalizeResumeId(this.resumeId);
      return `/admin/resume/save/${resumeId ?? ''}`;
    }

    if (
      this.mode === CreateResumeMode.CREATE ||
      this.mode === CreateResumeMode.COPY
    ) {
      return '/users/newCVCandidate';
    }

    return '/users/updateCVCandidate';
  }

  private get adminRequestConfig(): AxiosRequestConfig | undefined {
    if (this.mode !== CreateResumeMode.ADMIN_EDIT) {
      return undefined;
    }

    const tokenKey = Cookies.get('token');
    const adminID = Cookies.get('adminID');

    return {
      headers: {
        key: ApiKey,
        adminid: adminID,
        Authorization: `Bearer ${tokenKey}`,
      },
    };
  }

  private buildSubmitPayload(): SubmitResumePayload {
    const submitResumeId = this.submitResumeId ?? this.normalizeResumeId(this.resumeId);
    const desiredCity = this.getDesiredCityOption();

    return {
      ...(submitResumeId === undefined ? {} : { id: submitResumeId }),
      city: this.profileCity ?? desiredCity,
      position: this.toOption(
        this.desiredPosition.positionVM.value ??
          this.findOptionByName(
            this.desiredPosition.positionOptions,
            this.desiredPosition.position
          ),
        this.desiredPosition.position
      ),
      desired_category: this.desiredPosition.desiredCategories.map((group) => ({
        category: this.toNullableNumber(group.categoryId),
        sub_category: group.specializations.values.map((item) =>
          this.toOption({ id: item.id }, item.name)
        ),
      })),
      work_type: this.desiredPosition.workTypes.values.map((item) => ({
        value: item.value,
        label: item.label,
      })),
      salary_min: this.toNullableNumber(this.desiredPosition.salaryMin),
      salary_max: this.toNullableNumber(this.desiredPosition.salaryMax),
      desired_country: this.toNullableNumber(this.desiredPosition.desiredCountry),
      relocations: desiredCity ? [desiredCity] : [],
      salary_currency: this.toNullableNumber(this.desiredPosition.salaryCurrency),
      experiences: this.workExperience.experiences.map((experience) => ({
        position: this.toOption(
          experience.positionVM.value ??
            this.findOptionByName(experience.positionOptions, experience.position),
          experience.position
        ),
        organization: this.toOption(
          experience.organizationVM.value ??
            this.findOptionByName(
              experience.organizationOptions,
              experience.organization
            ),
          experience.organization
        ),
        industry: experience.industries.values.map((industry) =>
          this.toOption({ id: industry.id }, industry.name)
        ),
        exp_country: this.toNullableNumber(experience.expCountry),
        exp_city: experience.expCity
          ? this.toOption(
              experience.location.cityVM.value ??
                this.findCityByLabel(experience.location.cityOptions, experience.expCity),
              experience.expCity
            )
          : '',
        from_month_exp: this.toNullableNumber(experience.fromMonth),
        from_year_exp: this.toNullableNumber(experience.fromYear),
        to_month_exp: this.toNullableNumber(experience.toMonth),
        to_year_exp: this.toNullableNumber(experience.toYear),
        still_working: experience.stillWorking ? 1 : 0,
        achievements: experience.achievements,
      })),
      educations: this.educationInfo.educations.map((education) => ({
        edu_type: education.eduType,
        institution: this.toOption(
          education.institutionVM.value ??
            this.findOptionByName(education.institutionOptions, education.institution) ??
            { id: education.institutionId },
          education.institution
        ),
        faculty: this.toOption(
          education.facultyVM.value ??
            this.findOptionByName(education.facultyOptions, education.faculty),
          education.faculty
        ),
        specialty: this.toOption(
          education.specialtyVM.value ??
            this.findOptionByName(education.specialtyOptions, education.specialty),
          education.specialty
        ),
        from_month_edu: this.toNullableNumber(education.fromMonth),
        from_year_edu: this.toNullableNumber(education.fromYear),
        to_month_edu: this.toNullableNumber(education.toMonth),
        to_year_edu: this.toNullableNumber(education.toYear),
        still_learning: education.stillLearning,
      })),
      additional_educations: this.educationInfo.additionalEducations.map((education) => ({
        course_name: this.toNullableString(education.courseName),
        institution_add: this.toNullableString(education.institution),
        diploma_type: this.toNullableString(education.documentName),
        course_link: this.toNullableString(education.courseLink),
        certificate: education.certificate,
        from_month_edu_add: this.toNullableNumber(education.fromMonth),
        from_year_edu_add: this.toNullableNumber(education.fromYear),
        to_month_edu_add: this.toNullableNumber(education.toMonth),
        to_year_edu_add: this.toNullableNumber(education.toYear),
        still_learning_add: education.stillLearning,
      })),
      video_cv_link: this.additionalInfo.videoCvLink,
      about_yourself: this.additionalInfo.aboutYourself,
      skills: this.additionalInfo.skills.values.map((skill) =>
        this.toOption({ id: skill.id }, skill.name)
      ),
      languages: this.additionalInfo.languages
        .filter((language) => language.hasValue)
        .map((language) => ({
          lng: this.toNullableNumber(language.languageId),
          lng_skill: language.skillId,
        })),
      driver_license: this.additionalInfo.driverLicense ? 1 : 0,
      driver_category: this.additionalInfo.driverCategories.values.map((category) =>
        this.toOption({ id: category.id }, category.name)
      ),
      visibility: this.additionalInfo.visibility
        ? {
            value: this.additionalInfo.visibility.value,
            label: this.additionalInfo.visibility.label,
          }
        : null,
      company_whitelist: this.additionalInfo.companyWhitelist.selected.map((company) =>
        this.toOption({ id: company.id }, company.name)
      ),
      company_blacklist: this.additionalInfo.companyBlacklist.selected.map((company) =>
        this.toOption({ id: company.id }, company.name)
      ),
    };
  }

  private getDesiredCityOption(): ApiOption | null {
    if (!this.desiredPosition.desiredCity) {
      return null;
    }

    return this.toOption(
      this.desiredPosition.location.cityVM.value ??
        this.findCityByLabel(
          this.desiredPosition.location.cityOptions,
          this.desiredPosition.desiredCity
        ),
      this.desiredPosition.desiredCity
    );
  }

  private toOption(
    value: { id?: number | string | null } | null | undefined,
    label: string
  ): ApiOption {
    const id = value?.id;
    const trimmedLabel = label.trim();

    return {
      value:
        id === undefined || id === null || String(id).startsWith('custom:')
          ? trimmedLabel || null
          : id,
      label,
    };
  }

  private toApiOption(
    option: { value?: number | string | null; label?: string | null } | null | undefined
  ): ApiOption | null {
    if (option?.value === null || option?.value === undefined || !option.label) {
      return null;
    }

    return {
      value: option.value,
      label: option.label,
    };
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? null : numberValue;
  }

  private toNullableString(value: string | null | undefined): string | null {
    const trimmedValue = value?.trim() ?? '';
    return trimmedValue ? value ?? null : null;
  }

  private findOptionByName<TOption extends { name?: string | null }>(
    options: TOption[],
    label: string
  ): TOption | null {
    const normalizedLabel = this.normalizeLabel(label);

    if (!normalizedLabel) {
      return null;
    }

    return (
      options.find((option) => this.normalizeLabel(option.name ?? '') === normalizedLabel) ??
      null
    );
  }

  private findCityByLabel(options: CountryCityCity[], label: string): CountryCityCity | null {
    const normalizedLabel = this.normalizeLabel(label);

    if (!normalizedLabel) {
      return null;
    }

    return (
      options.find((city) => {
        const fullLabel = this.normalizeLabel(formatCountryCityLabel(city));
        const cityName = this.normalizeLabel(city.name);

        return fullLabel === normalizedLabel || cityName === normalizedLabel;
      }) ?? null
    );
  }

  private normalizeLabel(value: string): string {
    return value.trim().toLowerCase();
  }

  private resolveSubmitResumeId(
    response:
      | CandidateEditProfileEmptyResponse['response']
      | NewCVCandidateResponseDTO['response']
  ): number | string | undefined {
    const payload = response as {
      id?: unknown;
      cv_id?: unknown;
      resume_id?: unknown;
      userdetail?: {
        id?: unknown;
        cv_id?: unknown;
        resume_id?: unknown;
      };
    };

    return (
      this.normalizeResumeId(payload.id) ??
      this.normalizeResumeId(payload.cv_id) ??
      this.normalizeResumeId(payload.resume_id) ??
      this.normalizeResumeId(payload.userdetail?.id) ??
      this.normalizeResumeId(payload.userdetail?.cv_id) ??
      this.normalizeResumeId(payload.userdetail?.resume_id)
    );
  }

  private normalizeResumeId(value: unknown): number | string | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value !== 'string') {
      return undefined;
    }

    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? value : numberValue;
  }

  goBack(): void {
    if (!this.canGoBack) {
      return;
    }

    const prevStep = this.stepOrder[this.currentStepIndex - 1];
    if (prevStep) {
      this.activeStep = prevStep;
    }
  }

  goNext(): void {
    if (!this.canGoNext) {
      return;
    }

    this.markStepAttempted(this.activeStep);

    if (!this.isCurrentStepValid) {
      return;
    }

    const nextStep = this.stepOrder[this.currentStepIndex + 1];
    if (nextStep) {
      this.activeStep = nextStep;
    }
  }

  goToStep(step: CreateResumeStep): void {
    if (this.isStepUnlocked(step)) {
      this.activeStep = step;
    }
  }

  setActiveStep(step: CreateResumeStep): void {
    this.activeStep = step;
  }

  isStepUnlocked(step: CreateResumeStep): boolean {
    const targetIndex = this.stepOrder.indexOf(step);
    if (targetIndex <= 0) {
      return true;
    }

    for (let index = 0; index < targetIndex; index += 1) {
      const prevStep = this.stepOrder[index];
      if (!this.isStepValid(prevStep)) {
        return false;
      }
    }

    return true;
  }

  shouldShowStepErrors(step: CreateResumeStep): boolean {
    return this.attemptedSteps.has(step);
  }

  private markStepAttempted(step: CreateResumeStep): void {
    this.attemptedSteps.add(step);
  }

  private markAllStepsAttempted(): void {
    this.stepOrder.forEach((step) => {
      this.markStepAttempted(step);
    });
  }

  private focusFirstInvalidStep(): void {
    const firstInvalidStep = this.stepOrder.find((step) => !this.isStepValid(step));

    if (firstInvalidStep) {
      this.activeStep = firstInvalidStep;
    }
  }

  private isStepValid(step: CreateResumeStep): boolean {
    switch (step) {
      case CreateResumeStep.DESIRED_POSITION:
        return this.desiredPosition.valid;
      case CreateResumeStep.WORK_EXPERIENCE:
        return this.workExperience.valid;
      case CreateResumeStep.EDUCATION_INFO:
        return this.educationInfo.valid;
      case CreateResumeStep.ADDITIONAL_INFO:
        return this.additionalInfo.valid;
      default:
        return false;
    }
  }

  dispose(): void {
    this.valuesLoggerDisposer();
    this.desiredPosition.dispose();
    this.workExperience.dispose();
    this.educationInfo.dispose();
    this.additionalInfo.dispose();
  }
}
